import React from 'react';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import { CheckCircle, Error, Loop } from '@mui/icons-material';
import { formatCurrency } from '../../utils/validators';
import { Currency } from '../../types/PaymentLink';

interface PaymentStatusProps {
  status: 'success' | 'failed' | 'processing';
  amount?: number;
  currency?: Currency;
  errorMessage?: string;
  onRetry?: () => void;
}

const PaymentStatus: React.FC<PaymentStatusProps> = ({ status, amount, currency, errorMessage, onRetry }) => {
  if (status === 'processing') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
      >
        <Loop
          sx={{
            fontSize: 80,
            color: '#9c27b0',
            animation: 'spin 2s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography variant="h5" sx={{ mt: 3, fontWeight: 600 }}>
          Processing Payment...
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we process your payment securely.
        </Typography>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
      >
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            textAlign: 'center',
            boxShadow: 5,
            background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <CheckCircle
              sx={{
                fontSize: 100,
                color: '#4caf50',
                animation: 'scaleIn 0.5s ease-out',
                '@keyframes scaleIn': {
                  '0%': { transform: 'scale(0)' },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            />
            <Typography variant="h4" sx={{ mt: 3, fontWeight: 700, color: '#2e7d32' }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
              Thank you for your payment. Your transaction has been completed successfully.
            </Typography>
            {amount && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  border: '2px solid #4caf50',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Amount Paid
                </Typography>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                  {formatCurrency(amount, currency)}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              You will receive a confirmation email shortly.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (status === 'failed') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
      >
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            textAlign: 'center',
            boxShadow: 5,
            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Error
              sx={{
                fontSize: 100,
                color: '#f44336',
                animation: 'shake 0.5s ease-out',
                '@keyframes shake': {
                  '0%, 100%': { transform: 'translateX(0)' },
                  '25%': { transform: 'translateX(-10px)' },
                  '75%': { transform: 'translateX(10px)' },
                },
              }}
            />
            <Typography variant="h4" sx={{ mt: 3, fontWeight: 700, color: '#c62828' }}>
              Payment Failed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
              We were unable to process your payment. Please try again.
            </Typography>
            {errorMessage && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#ffffff',
                  borderRadius: 2,
                  border: '2px solid #f44336',
                  mb: 3,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Error Details
                </Typography>
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errorMessage}
                </Typography>
              </Box>
            )}
            {onRetry && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onRetry}
                sx={{ mt: 2 }}
              >
                Try Again
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return null;
};

export default PaymentStatus;
